import { useEffect, useRef, useState } from "react";
import { AiOutlinePlus } from "react-icons/ai";
import AdminLayout from "../../layout/AdminLayout";
import {
  addEntertainmentVideo,
  getEntertainmentVideoById,
  updateEntertainmentVideo,
  getAdminScreensForVideo,
} from "../../../api/entertainmentVideo.api";
import { errorMsg, success } from "../../../utils/notifications";
import { getActiveMobileLocations } from "../../../api/mobileLocation.api";
import Select from "react-select";
import { useNavigate, useParams } from "react-router-dom";
const initialData = {
  name: "",
  video: "",
  category: "",
  startDate: "",
  endDate: "",
  duration: "",
  location: "",
  status: "",
  screens: [],
};

const types = ["vehicle", "restaurant"];
const statusOptions = [
  { value: "pending", label: "Pending Approval" },
  { value: "approved", label: "Approved" },
  { value: "rejected", label: "Rejected" },
];
function AddEntertainmentVideo() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [videoData, setVideoData] = useState(initialData);
  const [selectedFile, setSelectedFile] = useState(null);
  const [locationTypes, setlocationTypes] = useState([]);
  const inputRef = useRef(null);
  const [availableScreens, setAvailableScreens] = useState([]);
  const [isLoadingScreens, setIsLoadingScreens] = useState(false);

  const serviceLocationOptions = locationTypes.map((location) => ({
    value: location._id, // This is the ID that will be sent to backend
    label: location.name, // This is the name that will be displayed
  }));

  useEffect(() => {
    if (videoData.type?.value && videoData.location?.value) {
      const fetchScreens = async () => {
        setIsLoadingScreens(true);
        try {
          // In the fetchScreens function:
          const response = await getAdminScreensForVideo(
            videoData.type.value,
            videoData.location.value
          );
          setAvailableScreens(response?.data?.filteredScreens || []); // Access filteredScreens from response
        } catch (error) {
          console.error("Error fetching screens:", error);
          errorMsg("Error", "Failed to fetch screens");
        } finally {
          setIsLoadingScreens(false);
        }
      };
      fetchScreens();
    } else {
      // Clear screens when no type or location is selected
      setAvailableScreens([]);
    }
  }, [videoData.type?.value, videoData.location?.value]); // Run when type or location changes

  useEffect(() => {
    const fetchData = async () => {
      try {
        const locationsResponse = await getActiveMobileLocations();
        setlocationTypes(locationsResponse?.data || []);

        if (id) {
          const videoResponse = await getEntertainmentVideoById(id);
          const videoDetails = videoResponse?.data;

          const formattedStartDate = videoDetails.startDate
            ? new Date(videoDetails.startDate).toISOString().split("T")[0]
            : "";
          const formattedEndDate = videoDetails.endDate
            ? new Date(videoDetails.endDate).toISOString().split("T")[0]
            : "";

          const locationOption = serviceLocationOptions.find(
            (option) => option.value === videoDetails.location
          ) || {
            value: videoDetails.location,
            label:
              videoDetails.locationName ||
              videoDetails.location ||
              "Unknown Location",
          };

          // Extract screen IDs from the video details
          const screenIds =
            videoDetails.screens?.map((screen) =>
              typeof screen === "object" ? screen._id : screen
            ) || [];

          // Set the initial video data including screens
          setVideoData({
            ...videoDetails,
            startDate: formattedStartDate,
            endDate: formattedEndDate,
            type: {
              value: videoDetails.type,
              label: videoDetails.type,
            },
            location: locationOption,
            video: videoDetails.video,
            status: videoDetails.status || "pending",
            screens: screenIds, // Set the screen IDs
          });

          // If there are screens, fetch them to show in the UI
          if (videoDetails.type && videoDetails.location) {
            setIsLoadingScreens(true);
            try {
              const response = await getAdminScreensForVideo(
                videoDetails.type,
                videoDetails.location
              );

              // Combine the fetched screens with any screens that might not be in the current location
              const allScreens = response?.data?.filteredScreens || [];

              setAvailableScreens(allScreens);
            } catch (error) {
              console.error("Error fetching screens:", error);
            } finally {
              setIsLoadingScreens(false);
            }
          }
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };

    fetchData();
  }, [id]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === "checkbox" ? checked : value;

    setVideoData({
      ...videoData,
      [name]: newValue,
    });
  };

  const handleScreenSelection = (screenId) => {
    setVideoData((prev) => {
      const newScreens = prev.screens.includes(screenId)
        ? prev.screens.filter((id) => id !== screenId)
        : [...prev.screens, screenId];
      return { ...prev, screens: newScreens };
    });
  };

  // Update the toggleAllScreens function
  const toggleAllScreens = () => {
    const currentScreenIds = availableScreens.map((screen) => screen._id);

    if (videoData.screens.length === availableScreens.length) {
      // Deselect only the screens from the current location
      setVideoData((prev) => ({
        ...prev,
        screens: prev.screens.filter((id) => !currentScreenIds.includes(id)),
      }));
    } else {
      // Add all current screens, keeping any previously selected screens from other locations
      setVideoData((prev) => ({
        ...prev,
        screens: [...new Set([...prev.screens, ...currentScreenIds])],
      }));
    }
  };

  const validateFile = (file) => {
    const maxFileSize = 100 * 1024 * 1024; // 100MB in bytes

    return new Promise((resolve, reject) => {
      if (file.size > maxFileSize) {
        return reject("Selected video exceeds 100MB file size limit.");
      }

      const video = document.createElement("video");
      video.onloadedmetadata = () => {
        const duration = video.duration;
        if (duration > 21) {
          return reject("Selected video exceeds 20 seconds duration.");
        }

        const width = video.videoWidth;
        const height = video.videoHeight;
        const maxWidth = 3840;
        const maxHeight = 2160;

        if (width >= maxWidth || height >= maxHeight) {
          return reject(
            `Video dimensions exceed maximum allowed: ${maxWidth}x${maxHeight}.`
          );
        }

        const isPortrait = height > width;
        const isLandscape = width > height;

        if (videoData.type?.value === "restaurant" && !isPortrait) {
          return reject("Please upload a portrait video.");
        }

        if (videoData.type?.value === "vehicle" && !isLandscape) {
          return reject("Please upload a landscape video.");
        }

        resolve();
      };

      video.src = URL.createObjectURL(file);
    });
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];

    validateFile(file)
      .then(() => setSelectedFile(file))
      .catch((err) => {
        setSelectedFile(null);
        errorMsg("Error", err);
      });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedFile && !videoData.video) {
      errorMsg(
        "Missing Video",
        "Please upload a valid video before submitting."
      );
      setLoading(false);
      return;
    }

    if (!id && videoData.screens.length === 0) {
      errorMsg("Error", "Please select at least one screen");
      return;
    }

    setLoading(true);
    const formData = new FormData();
    formData.append("name", videoData.name);
    if (selectedFile) {
      formData.append("video", selectedFile);
    }
    formData.append("type", videoData.type.value);
    formData.append("category", videoData.category);
    formData.append("startDate", videoData.startDate);
    formData.append("endDate", videoData.endDate);
    formData.append("duration", videoData.duration);
    formData.append("status", videoData.status);

    const matchedLocation = serviceLocationOptions.find(
      (option) => option.label === videoData.location?.label
    );
    formData.append(
      "location",
      matchedLocation?.value || videoData.location?.value || ""
    );
    formData.append("screenIds", JSON.stringify(videoData.screens));

    try {
      if (id) {
        await updateEntertainmentVideo(id, formData);
        success("Successful", "Entertainment Video Updated Successfully");
        navigate(`/infotainmentvideos/${id}`);
      } else {
        await addEntertainmentVideo(formData);
        success("Successful", "Entertainment Video Added Successfully");
        navigate("/infotainmentvideos");
      }
      setVideoData(initialData);
      setSelectedFile(null);
    } catch (err) {
      errorMsg("Oops", err);
    } finally {
      setLoading(false);
    }
  };
  const resetVideo = () => {
    setSelectedFile(null);
    setVideoData({ ...videoData, video: "" });
    if (inputRef.current) {
      inputRef.current.value = "";
    }
  };

  return (
    <AdminLayout
      title={id ? "Update Infotainment Video" : "Add Infotainment Video"}
    >
      <form onSubmit={handleSubmit} method="POST">
        <div className="gap-5 px-3 md:px-5 lg:px-5 py-0.5 md:py-1.5 lg:py-1.5 grid grid-cols-1 lg:grid-cols-12">
          {/* Left side for other input fields */}
          <div className="lg:col-span-8 md:col-span-12 col-span-12 px-1 lg:px-5 md:px-5">
            <div className="gap-4 py-0.5 md:py-3 lg:py-3 grid grid-cols-1 sm:grid-cols-12 lg:grid-cols-12 md:grid-cols-12">
              {/* Name Input */}
              <div className="lg:col-span-6 md:col-span-6 col-span-12">
                <label
                  htmlFor="name"
                  className="block text-base font-semibold leading-6 text-gray-600"
                >
                  Name
                </label>
                <input
                  type="text"
                  name="name"
                  placeholder="Enter Name"
                  value={videoData.name}
                  onChange={handleChange}
                  id="name"
                  className="text-sm font-medium bg-white bg-opacity-80 px-3 block w-full rounded-md outline-0 py-2 text-gray-600 placeholder:text-gray-300 border-2 border-gray-200 border-opacity-70 focus:border-2 focus:border-yellow sm:leading-6 focus:ring-transparent"
                  required
                />
              </div>

              {/* Type Select Dropdown */}
              <div className="lg:col-span-6 md:col-span-6 col-span-12">
                <label
                  htmlFor="type"
                  className="block text-base font-semibold leading-6 text-gray-600"
                >
                  Type
                </label>
                <Select
                  name="type"
                  id="type"
                  options={types.map((type) => ({
                    value: type,
                    label: type.charAt(0).toUpperCase() + type.slice(1),
                  }))}
                  value={videoData.type}
                  className="basic-multi-select"
                  classNamePrefix="select Type"
                  onChange={(selectedOption) =>
                    setVideoData({ ...videoData, type: selectedOption })
                  }
                  required
                  isDisabled={!!id}
                />
              </div>

              {/* Start Date Input */}
              <div className="lg:col-span-6 md:col-span-6 col-span-12 mt-4">
                <label
                  htmlFor="startDate"
                  className="block text-base font-semibold leading-6 text-gray-600"
                >
                  Start Date
                </label>
                <input
                  type="date"
                  name="startDate"
                  value={videoData.startDate}
                  onChange={handleChange}
                  className="text-sm font-medium bg-white bg-opacity-80 px-3 block w-full rounded-md outline-0 py-2 text-gray-600 placeholder:text-gray-300 border-2 border-gray-200 border-opacity-70 focus:border-2 focus:border-yellow sm:leading-6 focus:ring-transparent"
                  required
                  min={new Date().toISOString().split("T")[0]}
                  disabled={!!id}
                />
              </div>

              {/* End Date Input */}
              <div className="lg:col-span-6 md:col-span-6 col-span-12 mt-4">
                <label
                  htmlFor="endDate"
                  className="block text-base font-semibold leading-6 text-gray-600"
                >
                  End Date
                </label>
                <input
                  type="date"
                  name="endDate"
                  value={videoData.endDate}
                  onChange={handleChange}
                  className="text-sm font-medium bg-white bg-opacity-80 px-3 block w-full rounded-md outline-0 py-2 text-gray-600 placeholder:text-gray-300 border-2 border-gray-200 border-opacity-70 focus:border-2 focus:border-yellow sm:leading-6 focus:ring-transparent"
                  required
                  min={videoData.startDate}
                />
              </div>

              {/* Location Select */}
              <div className="lg:col-span-6 md:col-span-6 col-span-12">
                <label
                  htmlFor="location"
                  className="block text-base font-semibold leading-6 mb-1 text-gray-600"
                >
                  Location
                </label>
                <Select
                  name="location"
                  id="location"
                  options={serviceLocationOptions}
                  value={videoData.location}
                  className="basic-multi-select"
                  classNamePrefix="select Location"
                  onChange={(selectedOption) =>
                    setVideoData({ ...videoData, location: selectedOption })
                  }
                  required
                />
              </div>

              <div className="lg:col-span-6 md:col-span-6 col-span-12">
                <label
                  htmlFor="category"
                  className="block text-base font-semibold mb-1 leading-6 text-gray-600"
                >
                  Category
                </label>
                <Select
                  name="category"
                  id="category"
                  options={[
                    { value: "Entertainment", label: "Entertainment" },
                    { value: "Information", label: "Information" },
                  ]}
                  value={{
                    value: videoData?.category,
                    label: videoData?.category,
                  }}
                  className="basic-multi-select"
                  classNamePrefix="select category"
                  onChange={(selectedOption) =>
                    setVideoData({
                      ...videoData,
                      category: selectedOption?.value,
                    })
                  }
                  required
                  isDisabled={!!id}
                />
              </div>
            </div>

            <div className="lg:col-span-6 md:col-span-6 col-span-12">
              <label
                htmlFor="status"
                className="block text-base font-semibold leading-6 text-gray-600"
              >
                Status
              </label>
              <Select
                name="status"
                id="status"
                options={statusOptions}
                value={statusOptions.find(
                  (option) => option.value === videoData.status
                )}
                className="basic-multi-select"
                classNamePrefix="select Status"
                onChange={(selectedOption) =>
                  setVideoData({
                    ...videoData,
                    status: selectedOption.value,
                  })
                }
                required
              />
            </div>

            <div className="lg:col-span-12 md:col-span-12 col-span-12 mt-6">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-lg font-semibold text-gray-700">
                  Select Screens
                </h3>
                {availableScreens.length > 0 && (
                  <button
                    type="button"
                    onClick={toggleAllScreens}
                    className="text-sm text-blue-600 hover:text-blue-800"
                  >
                    {videoData.screens.length === availableScreens.length
                      ? "Deselect All"
                      : "Select All"}
                  </button>
                )}
              </div>

              {isLoadingScreens ? (
                <div className="flex justify-center py-4">
                  <span>Loading screens...</span>
                </div>
              ) : availableScreens.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="min-w-full bg-white rounded-lg overflow-hidden">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Select
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Model
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          UUID
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Status
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {availableScreens.map((screen) => (
                        <tr key={screen._id}>
                          <td className="px-4 py-2 whitespace-nowrap">
                            <input
                              type="checkbox"
                              checked={videoData.screens.includes(screen._id)}
                              onChange={() => handleScreenSelection(screen._id)}
                              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                            />
                          </td>
                          <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-500">
                            {screen.model || screen.name || "N/A"}
                          </td>
                          <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-500">
                            {screen.uuid || screen.id || "N/A"}
                          </td>
                          <td className="px-4 py-2 whitespace-nowrap">
                            <span
                              className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                                (screen.status || screen.state) === "Active"
                                  ? "bg-green-100 text-green-800"
                                  : "bg-red-100 text-red-800"
                              }`}
                            >
                              {screen.status || screen.state || "Unknown"}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-4 text-gray-500">
                  {videoData.type?.value
                    ? "No screens available for this type"
                    : "Select a type to see available screens"}
                </div>
              )}
            </div>
          </div>

          {/* Right side for Add Video */}
          <div className="lg:col-span-4 md:col-span-12 col-span-12 px-1 lg:px-5 md:px-5">
            <div className="flex flex-col items-end">
              <div className="flex items-center justify-between w-full">
                <label
                  htmlFor="video"
                  className="block text-base font-semibold leading-6 text-gray-600"
                >
                  Add Video
                </label>
                <button
                  type="button"
                  className="flex justify-center rounded-md border border-gray-400 py-1 px-4 text-xs leading-6 text-gray-800 shadow-sm hover:bg-opacity-90"
                  onClick={resetVideo}
                >
                  Reset Video
                </button>
              </div>

              <label
                htmlFor="video"
                className="border-2 border-dashed border-gray-300 h-72 w-full mt-2 flex items-center justify-center cursor-pointer"
              >
                {selectedFile ? (
                  <video
                    src={URL.createObjectURL(selectedFile)}
                    className="h-full w-full object-cover"
                    controls
                    autoPlay
                    loop
                    muted
                    onCanPlay={(e) => e.target.play()}
                  />
                ) : videoData.video ? (
                  <video
                    src={videoData.video}
                    className="h-full w-full object-cover"
                    controls
                    autoPlay
                    loop
                    muted
                    onCanPlay={(e) => e.target.play()}
                  />
                ) : (
                  <AiOutlinePlus className="text-gray-400 h-14 w-14 p-2" />
                )}
              </label>

              <input
                ref={inputRef}
                type="file"
                name="video"
                id="video"
                className="hidden"
                accept="video/*"
                onChange={handleFileChange}
              />
              {videoData?.type?.value && !selectedFile && !videoData.video && (
                <p className="text-base text-red-500 mt-2 me-auto">
                  Note: Please upload a{" "}
                  {videoData.type.value === "restaurant"
                    ? "portrait"
                    : videoData.type.value === "vehicle"
                    ? "landscape"
                    : "correct"}{" "}
                  video.
                </p>
              )}
            </div>
          </div>
        </div>
        <div className="flex justify-end items-center mt-4 mr-10">
          <button
            type="submit"
            disabled={loading}
            className={`flex items-center bg-yellow-500 hover:bg-yellow-600 text-white py-2 px-4 rounded ${
              loading ? "cursor-not-allowed" : ""
            }`}
          >
            {loading ? (
              <span>Loading...</span>
            ) : (
              <>
                <AiOutlinePlus className="mr-2" />{" "}
                {id ? "Update Video" : "Add Video"}
              </>
            )}
          </button>
        </div>
      </form>
    </AdminLayout>
  );
}

export default AddEntertainmentVideo;
